import {
  useEffect,
  useRef,
} from 'react'

import {
  AccessTokenStore,
  TokenRefresher,
  reportAuthFailure,
} from '@termly-web/common'

import getRefreshDelay from './getRefreshDelay'

const RETRY_DELAY_MS = 30_000


/**
 * Replaces the access token shortly before it expires, for as long as the page
 * is open.
 *
 * The interceptors already recover from an expired token, so this exists for
 * the user rather than for correctness: a token replaced ahead of time spares
 * whoever is working a request that fails, refreshes and replays itself while
 * they wait. Nothing depends on this having run.
 *
 * Mount it once. Each mount arms a timer of its own and every one of them
 * fires, so a second mount buys a duplicate refresh rather than a spare.
 */
export default function useProactiveRefresh() {
  const timerID = useRef()

  useEffect(() => {
    const armTimer = (delay) => {
      clearTimeout(timerID.current)

      if ( delay === void 0 ) {
        return
      }

      timerID.current = setTimeout(() => {
        TokenRefresher.refresh()
          .catch((error) => {
            // The dead token stays where it is. The next request carrying it
            // comes back 401 and the response interceptor decides from there
            // whether the session is over, because losing one refresh to a
            // flaky connection is not reason enough to throw someone out.
            //
            // The cost we are accepting: this fires every few minutes in every
            // open tab, so sleeping laptops and flaky connections will report
            // network failures that no user ever saw. Judged worth it to keep
            // a misbehaving auth service visible. Should the volume prove bad,
            // the Sentry filter is the place to deal with it rather than going
            // silent here.
            reportAuthFailure(error)

            // Without this the scheduler would retire on its first failure and
            // stay retired: a refresh that throws writes nothing to the store,
            // so no notification arrives to re-arm it, and the tab spends the
            // rest of the token's life with no timer at all.
            //
            // Retrying covers an unauthorized refresh too, which is not the
            // dead end it looks like. Tabs restored together hold tokens of
            // near-identical age, so their timers fire together, and the server
            // rotates the refresh cookie on the first one through, leaving the
            // others holding a spent credential. By the time this retry runs,
            // the rotated cookie is the one it sends.
            //
            // A fixed interval rather than a fresh `getRefreshDelay()`, which
            // returns half the remaining life and so would halve again on each
            // retry. That interval converges on expiry without ever reaching
            // it, which is unbounded retries in a finite window.
            if ( getRefreshDelay() === void 0 ) {
              return
            }

            armTimer(RETRY_DELAY_MS)
          })
      }, delay)
    }

    const scheduleRefresh = () => {
      armTimer(getRefreshDelay())
    }

    scheduleRefresh()

    // The store is the only thing that knows the token changed, and it changes
    // for reasons that have nothing to do with this timer: the startup handoff
    // or refresh writes the first one, a 401 replaces a dead one, a logout
    // drops it. Every one of those invalidates the schedule, so re-arm on all
    // of them.
    const unsubscribe = AccessTokenStore.subscribe(scheduleRefresh)

    return () => {
      unsubscribe()

      clearTimeout(timerID.current)
    }
  }, [])
}
